<script setup>
import { inject, ref } from 'vue';
import Spinner from '../Spinner.vue';
import { onMounted } from 'vue';
import { onUnmounted } from 'vue';
import { createPostMessageChannel, safeParsePostMessage }  from '@/utils/postMessageHelpers'
import { captureException } from '@sentry/vue';

const COLLABORA_ONLINE_URL = import.meta.env.VITE_COLLABORA_ONLINE_URL;
const SLIDE_GENERATE_SUBMIT_API = import.meta.env.VITE_SLIDE_GENERATE_SUBMIT_API;
const SLIDE_GENERATE_CHECK_STATUS_API = import.meta.env.VITE_SLIDE_GENERATE_CHECK_STATUS_API;

const props = defineProps(['presentationId', 'isSlideGenerationLoading']);
const emit = defineEmits(['onSidebarClosed', 'onSlideGenerationUpdate']);
defineExpose({ open, close, isOpened, apply, dismiss });

const isOpen = ref(false);
const slidesRange = ref("");
const promptQuery = ref("");
const selectedOption = ref("1-3");
const generationStatus = ref("pending");
const authToken = ref(null);
const buttons = ref([
  { text: 'Option 1', value: 'Custom prompt for Option 1' },
  { text: 'Option 2', value: 'Custom prompt for Option 2' },
  { text: 'Option 3', value: 'Custom prompt for Option 3' }
]);
let slidesData = null;
const document_url = ref(null);

const isDocumentLoadedRef = inject('isDocumentLoadedRef');
const { editorIframeRef } = inject('editorIframeRef');

onMounted(() => {
  window.addEventListener('message', handlePostMessage, false);
});

onUnmounted(() => {
  window.removeEventListener('message', handlePostMessage);
});

function open() {
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
}

function isOpened() {
  return isOpen.value;
}


function parseSlidesRange(rangeString) {
  // Split the string by commas to handle individual ranges or numbers
  const parts = rangeString.split(',');

  // Use a Set to store unique numbers
  const resultSet = new Set();

  // Iterate over each part
  for (const part of parts) {
    if (part.includes('-')) {
      // Handle ranges (e.g., "1-3")
      const [start, end] = part.split('-').map(Number);
      for (let i = start; i <= end; i++) {
        resultSet.add(i); // Add each number in the range to the Set
      }
    } else {
      // Handle single numbers (e.g., "5")
      resultSet.add(Number(part)); // Add the single number to the Set
    }
  }

  // Convert the Set back to an array and sort it (optional)
  const resultArray = Array.from(resultSet).sort((a, b) => a - b);

  return resultArray;
}

async function handlePostMessage(e) {
  var msg = safeParsePostMessage(e.data);
  if (
    msg.MessageId === "CallPythonScript-Result"
    && msg.Values.commandName.startsWith("vnd.sun.star.script:InsertMultipleSlides.py$GetAllText")
  ) {
    if (msg.Values.success) {
      const response = JSON.parse(msg.Values.result.value);
      slidesData = response.data;
    } else {
      generationStatus.value = "failed";
      emit('onSlideGenerationUpdate', generationStatus.value);
    }
  } else if (msg.MessageId === "Generate_Upgrade_Slides") {
    await submitQuery("upgrade");
  } else if (msg.MessageId === "Generate_Upgrade_Blurred") {
    await submitQuery("upgrade_blurred");
  }
}

// Send a message and wait for a reply
async function getAuthToken() {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener('message', handleResponse);
      resolve("");
    }, 3000); // 3 second timeout

    // Set up a listener for the response
    const handleResponse = (event) => {
      var msg = safeParsePostMessage(event.data);
      if (msg.MessageId === 'Request_Auth_Token_Response') {
        window.removeEventListener('message', handleResponse);
        clearTimeout(timeoutId);
        if (msg.Success) {
          resolve(msg.Values.token);
        } else {
          resolve("");
        }
      }
    };

    window.addEventListener('message', handleResponse);

    parent.postMessage({
      "MessageId": "Request_Auth_Token",
      "SendTime": Date.now(),
      "Values": {},
      "Origin": location.origin,
    }, "*");
  });
}

async function waitForDocumentLoad(maxAttempts = 15, retryDelay = 2000) {
  let attempts = 0;

  while (attempts < maxAttempts) {
    if (isDocumentLoadedRef.value) {
      console.log('Document loaded successfully.');
      return true;
    }

    attempts++;
    console.log(`Document not loaded yet. Attempt ${attempts}/${maxAttempts}. Retrying in ${retryDelay}ms...`);

    if (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }

  throw new Error('Failed to load document within maximum attempts.');
}

async function submitQuery(flag) {
  try {
    // 1. Wait for the document to be ready before proceeding
    await waitForDocumentLoad();

    console.log(selectedOption.value);
    generationStatus.value = "loading";
    emit('onSlideGenerationUpdate', generationStatus.value);

    authToken.value = await getAuthToken();
    console.log('Received Token!!');

    if (flag && (flag == "upgrade" || flag == "upgrade_blurred")) {
      let slidesCount = await countSlides();
      slidesRange.value = `0-${slidesCount}`;
    }
    // 2. Proceed with postMessage only if document load check passed
    document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
      "MessageId": "CallPythonScript",
      "SendTime": Date.now(),
      "ScriptFile": "InsertMultipleSlides.py",
      "Function": "GetAllText",
      "Values": {
        "slide_numbers_json": { "type": "string", "value": JSON.stringify(parseSlidesRange(slidesRange.value)) }
      }
    }), COLLABORA_ONLINE_URL);

    const postMessageResponse = new Promise((resolve, reject) => {
      // Added a timeout safety to this interval as well to prevent infinite hanging
      const timeout = setTimeout(() => {
        clearInterval(interval);
        reject(new Error("Timeout waiting for slidesData response"));
      }, 30000);

      const interval = setInterval(() => {
        if (slidesData) {
          clearTimeout(timeout);
          clearInterval(interval);
          resolve(slidesData);
        }
      }, 100);
    });

    // 3. Await the response cleanly
    const result = await postMessageResponse;

    const url = SLIDE_GENERATE_SUBMIT_API;
    const jsonData = {
      "data": {
        "presentation_id": props.presentationId,
        "cookie": authToken.value,
        "number_of_slides": selectedOption.value,
        "text_input": promptQuery.value,
        "slides": result,
      }
    }
    if (flag !== undefined) {
      jsonData.data[flag] = true;
    }
    const options = {
      method: 'POST',
      body: JSON.stringify(jsonData)
    };

    const response = await fetch(url, options);
    const data = await response.json();
    console.log(data);
    checkJobStatus(data.user_id, data.item_name);

  } catch (error) {
    generationStatus.value = "failed";
    emit('onSlideGenerationUpdate', generationStatus.value);
    console.error("Submit Query Failed:", error);
    captureException(
      error,
      {
        level: "error",
        extra: {
          slidesData: slidesData,
        }
      }
    );
  }
}

function checkJobStatus(user_id, jobId) {
  const MAX_ATTEMPTS = 100;
  const POLL_INTERVAL = 5000;
  let attempts = 0;

  let url = SLIDE_GENERATE_CHECK_STATUS_API;
  console.log('Polling URL:', url);
  console.log('Starting polling jobID:', jobId);

  const polling = setInterval(function () {
    console.log('Polling ITEM_STATUS:', attempts + 1);
    let xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', 'Bearer ' + authToken.value);

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4) {
        let data = JSON.parse(xhr.responseText);
        console.log('Received data:', data);
        if (data && data.item_status && data.item_status === "ITEM_PUBLISHED" && data.item_link && data.item_link !== "testeLink") {

          console.log('Job completed');
          clearInterval(polling);

          console.log('Presentation ready! Redirecting...');
          console.log(data.item_link);
          document_url.value = atob(data.item_link);
          generationStatus.value = "finished";
          emit('onSlideGenerationUpdate', generationStatus.value);

          // this document_url is the base64 encoded string that will have the location of the new slides

        } else if (++attempts >= MAX_ATTEMPTS) {
          clearInterval(polling);
          displayError("Job too long.");
        }
        /*
         *         else {
         *           console.error('redirectXhr failed:', redirectXhr.statusText);
         *         } */
      }
    };

    xhr.send(JSON.stringify({
      user_id: user_id,
      item_name: jobId
    }));
  }, POLL_INTERVAL);

}

// Send message to iframe
const countSlides = async () => {
  const iframeWindow = editorIframeRef.value.contentWindow;
  const response = await createPostMessageChannel(
    iframeWindow,
    COLLABORA_ONLINE_URL,
    JSON.stringify({
      "MessageId": "Count_Slides",
      "SendTime": Date.now(),
      "Values": {}
    }),
    (msg) => msg.MessageId === 'Count_Slides_Resp',
    {
      maxRetries: 3
    }
  );
  const slidesCount = response.Values.slidesCount;
  return slidesCount;
};


function selectButton(index) {
  this.selectedButton = index;
  this.promptQuery = this.buttons[index].value; // Update prompt query based on selected button
}

function apply() {
  document.getElementById("coolframe").contentWindow.postMessage(JSON.stringify({
    "MessageId": "CallPythonScript",
    "SendTime": Date.now(),
    "ScriptFile": "InsertMultipleSlides.py",
    "Function": "AddSlidesFromS3",
    "Values": {
      "bucket_name": { "type": "string", "value": "generated-presentations" },
      "file_path": { "type": "string", "value": document_url.value }
    }
  }), COLLABORA_ONLINE_URL);
  console.log("ADDING GENERATED SLIDES");
  dismiss();
}

function dismiss() {
  generationStatus.value = "pending";
  emit('onSlideGenerationUpdate', generationStatus.value);
}
</script>

<template>
  <div id="slideGenerationSidebar" v-if="isOpen"
       style="position: absolute; top: 0%; bottom: 0%; right: 0%; left: 0%; display: flex; flex-direction: column; background: #f5f5f5; color: #333;">
    <div class="px-4 py-3 is-flex is-justify-content-space-between is-align-content-center has-background-white">
      <header class="has-text-weight-semibold is-size-5">
        Generate Slides
      </header>
      <a class="is-flex is-align-content-center" @click="() => { emit('onSidebarClosed'); }">
        <img src="@/assets/images/x-close.svg" style="filter: invert(0.5);">
      </a>
    </div>
    <div style="overflow-y: auto; overflow-x: hidden; flex-grow: 1; padding: 1rem;">
      <div v-if="generationStatus === 'pending'"
           class="is-flex is-flex-direction-column is-justify-content-start is-align-items-center">
        <!-- Slides Range Input -->
        <div class="field mb-3" style="width: 100%;">
          <label class="label has-text-dark">Relevant context</label>
          <div class="control">
            <input class="input" type="text" v-model="slidesRange"
                   placeholder="Enter page range  (e.g., 1-3,5,8-10)">
          </div>
        </div>

        <div class="field mb-3" style="width: 100%;">
          <label class="label has-text-dark">Instruction</label>
          <div class="control">
            <textarea v-model="promptQuery" class="textarea" placeholder=""></textarea>
          </div>
        </div>

        <!-- Prompt Query Input with Buttons -->
        <div class="field" style="width: 100%;">
          <label class="label has-text-dark">Desired slides count</label>
          <div class="control" style="width: 100%;">
            <div class="select" style="width: 100%;--bulma-arrow-color: #888">
              <select v-model="selectedOption" style="width: 100%">
                <option value="1-3">1-3 Slides</option>
                <option value="3-5">3-5 Slides</option>
                <option value="5-10">5-10 Slides</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="field pt-4" style="width: 100%;">
          <div class="control">
            <button class="button is-primary is-fullwidth"
                    :class="{ 'is-loading': props.isSlideGenerationLoading }" @click="submitQuery()"
                    style="background: #4a4a4a; color: white; border: none;">
              Submit
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="generationStatus === 'loading'"
           class="is-flex is-flex-direction-column is-justify-content-start is-align-items-center">
        <label class="label mt-2 has-text-dark">We will notify you when the slides are generated. You can resume your work in
          the meantime.</label>
        <Spinner class="m-4" style="font-size: 2.5rem" />
      </div>
      <div v-else-if="generationStatus === 'finished'"
           class="is-flex is-flex-direction-column is-justify-content-start is-align-items-center">
        <div class="field pt-4" style="width: 100%;">
          <label class="label has-text-dark">Slides generated !</label>
          <div class="control">
            <button class="button is-primary is-fullwidth" @click="apply"
                    style="background-image: linear-gradient(45deg, #E100FF 0%, #7F00FF 100%); color: white; border: none;">
              Apply
            </button>
          </div>
        </div>
        <div class="field" style="width: 100%;">
          <div class="control">
            <button class="button is-fullwidth" @click="dismiss()">
              Dismiss
            </button>
          </div>
        </div>
      </div>
      <div v-else-if="generationStatus === 'failed'"
           class="is-flex is-flex-direction-column is-justify-content-start is-align-items-center">
        <div class="field pt-4" style="width: 100%;">
          <label class="label has-text-dark">Failed to generate slides.</label>
        </div>
        <div class="field" style="width: 100%;">
          <div class="control">
            <button class="button is-fullwidth" @click="dismiss()">
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
 #slideGenerationSidebar div:has(header) {
   border-bottom: 1px solid #ddd;
 }

 .input,
 .textarea {
   background-color: white;
   border: 1px solid #ddd;
   color: #333;
 }

 .input::placeholder,
 .textarea::placeholder {
   color: #999;
 }

 .button.is-primary {
   background: #4a4a4a;
   font-weight: bold;
 }

 .button.is-primary:hover {
   background: #333;
 }

 .prompt-button {
   padding: 0.25rem 0.5rem;
   border: 1px solid #ddd;
   border-radius: 4px;
   background-color: white;
   color: #333;
   font-size: 0.875rem;
   cursor: pointer;
   transition: background 0.3s, border-color 0.3s;
 }

 .prompt-button.selected {
   background: linear-gradient(135deg, #6e48aa, #9d50bb);
   border-color: #6e48aa;
   color: white;
 }

 .prompt-button:hover {
   border-color: #6e48aa;
 }
</style>
